import { useCallback } from 'react'
import { RecommendPromiseClient } from '@trustero/trustero-api-web/lib/recommend/recommend_grpc_web_pb'
import {
  GetSuggestedRequest,
  ResetSuggestedEvidenceRequest,
  ResetSuggestedTestsRequest,
  SUGGESTION_TYPES,
  SuggestedEvidenceResponse,
} from '@trustero/trustero-api-web/lib/recommend/recommend_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import log from 'loglevel'
import { useGrpcRevalidateByMethod } from '../useGrpcMutate'
import { GrpcResponse, NewGrpcResponse } from '../hooks/types'
import { useSwrGrpc } from '../useSwrGrpc'

export const useSuggestedEvidenceTable = (
  request: GetSuggestedRequest,
  shouldFetch = true,
): GrpcResponse<SuggestedEvidenceResponse> => {
  const { response } = useSwrGrpc(
    RecommendPromiseClient.prototype.getSuggestedEvidenceFromTable,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useRegenerateSuggestions = (
  controlId: string,
): ((suggestionType?: SUGGESTION_TYPES) => Promise<void>) => {
  const recommendClient = useAuthorizedGrpcClient(RecommendPromiseClient)
  const revalidate = useInvalidateSuggestedEvidence()

  return useCallback(
    async (suggestionType?: SUGGESTION_TYPES) => {
      try {
        if (suggestionType === SUGGESTION_TYPES.TESTS || !suggestionType) {
          const request = new ResetSuggestedTestsRequest()
          request.setControlId(controlId)
          await recommendClient.resetSuggestedTests(request)
        }
        if (suggestionType === SUGGESTION_TYPES.EVIDENCE || !suggestionType) {
          const request = new ResetSuggestedEvidenceRequest()
          request.setControlId(controlId)
          await recommendClient.resetSuggestedEvidence(request)
        }
        await revalidate()
      } catch (err) {
        log.error(
          `Failed to reset suggestions for control: ${controlId} err: ${err}`,
        )
      }
    },
    [controlId, recommendClient, revalidate],
  )
}

export const useInvalidateSuggestedEvidence = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()
  return useCallback(async () => {
    await Promise.all([
      mutateFunc(
        RecommendPromiseClient.prototype.getSuggestedEvidenceFromTable,
      ),
      mutateFunc(RecommendPromiseClient.prototype.getSuggestedTestFromTable),
    ])
  }, [mutateFunc])
}
